<template>
  <div
    class="ext-card"
    :class="{ 'is-recommended': recommended, 'is-plan-applied': planApplied }"
  >
    <div class="ext-top">
      <div>
        <div
          v-if="planApplied"
          class="ext-plan-badge"
        >
          設定中
        </div>
        <div class="ext-name">
          {{ courseName }}
        </div>
        <div class="ext-meta">
          <template v-if="durationLabel">
            {{ durationLabel }} ｜
          </template>
          {{ candidate.courseHistoryTemp.size }}名
        </div>
        <div
          v-if="planApplied"
          class="ext-plan-note"
        >
          会計まで有効（延長のたびにこの内容が使われます）
        </div>
      </div>

      <div class="ext-price-area">
        <v-progress-circular
          v-if="isCalculating"
          indeterminate
          size="16"
          width="2"
        />
        <template v-else-if="estimatedTotal !== null && estimatedTotal !== undefined">
          <div class="ext-price">
            ¥{{ Number(estimatedTotal).toLocaleString() }}
          </div>
          <div class="ext-price-sub">
            注文後のお会計
          </div>
        </template>
      </div>
    </div>

    <div class="ext-lines">
      <div
        v-for="(line, lineIdx) in lines"
        :key="`line-${lineIdx}`"
        class="ext-line"
      >
        <div class="ext-line-main">
          <span class="ext-line-name">{{ line.name }}</span>
          <!-- 単価×数量は2名以上のときだけ表示（1名だと小計と同額で重複表示になるため） -->
          <span
            v-if="line.temp.size > 1"
            class="ext-line-calc"
          >
            ¥{{ Number(line.temp.value.sellingPrice || 0).toLocaleString() }}
            × {{ line.temp.size }}
          </span>
          <span class="ext-line-sub">
            ¥{{ ((line.temp.value.sellingPrice || 0) * line.temp.size).toLocaleString() }}
          </span>
        </div>

        <v-btn-toggle
          :value="line.temp.value.taxCharge"
          mandatory
          dense
          class="ext-tax-toggle"
          :aria-label="`${line.name}の料金区分`"
          @change="line.temp.value.taxCharge = $event"
        >
          <v-btn
            v-for="taxOption in taxOptions"
            :key="taxOption.value"
            :value="taxOption.value"
            small
            :ripple="false"
            :disabled="isTaxOptionDisabled(taxOption)"
            :title="isTaxOptionDisabled(taxOption) ? includedUnavailableHint : null"
            :aria-label="taxOption.label"
          >
            {{ taxOption.label }}
          </v-btn>
        </v-btn-toggle>

        <!-- 保存済みプラン等で既に込みが選ばれている行は勝手に書き換えず、送信前に気づけるよう警告だけ出す -->
        <div
          v-if="line.contributorMissing"
          class="ext-line-alert"
        >
          計上先がいないため、このままでは延長できません
        </div>
      </div>
    </div>

    <div class="ext-actions mt-3">
      <v-btn
        outlined
        class="ext-edit-btn"
        :ripple="false"
        :disabled="submitting"
        aria-label="編集"
        @click="$emit('edit')"
      >
        編集
      </v-btn>

      <v-btn
        color="primary"
        class="ext-submit-btn"
        :ripple="false"
        :loading="submitting"
        :disabled="submitting || disabled"
        aria-label="延長する"
        @click="$emit('submit')"
      >
        延長する
      </v-btn>
    </div>

    <!-- 解除は破棄操作なので主導線から離して控えめに置く -->
    <div
      v-if="planApplied"
      class="ext-plan-release"
    >
      <v-btn
        text
        small
        :ripple="false"
        :loading="releasingPlan"
        :disabled="releasingPlan"
        aria-label="設定を解除"
        @click="$emit('release-plan')"
      >
        設定を解除
      </v-btn>
    </div>
  </div>
</template>

<script>
import { computed, inject } from '@vue/composition-api'
import { find, isEmpty } from 'lodash'

import useCurrentData from '@/views/composable/useCurrentData'
import useVendingHistory from '@/views/composable/useVendingHistory'

const INCLUDED_UNAVAILABLE_HINT = '売上の計上先となる指名がないため込みは選べません'

const findVendible = (vendibles, vendibleId) => {
  return find(vendibles, vendible => Number(vendible.id) === Number(vendibleId))
}

export default {
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    estimatedTotal: {
      type: [Number, String],
      default: null,
    },
    isCalculating: {
      type: Boolean,
      default: false,
    },
    recommended: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    // 自動延長の処理中など、送信自体を受け付けない状態
    disabled: {
      type: Boolean,
      default: false,
    },
    // 設定中の延長プランで内容を上書きしたカード
    planApplied: {
      type: Boolean,
      default: false,
    },
    releasingPlan: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const courses = inject('courses', [])
    const nominations = inject('nominations', [])
    const items = inject('items', [])

    const { currentClub } = useCurrentData()
    const { resolveVendingContributorIds } = useVendingHistory()

    // NOTE: 「込み」は税を負担する売上計上先が必要。計上先の導出は自動セットと同じ関数に寄せる
    const hasNoContributor = computed(() => {
      const { candidate } = props

      const targetVendingHistoriesTemp = [
        candidate.courseHistoryTemp,
        ...candidate.nominationHistoriesTemp,
        ...candidate.itemHistoriesTemp,
        ...(candidate.newHistoriesTemp || []),
      ]

      return isEmpty(resolveVendingContributorIds({ targetVendingHistoriesTemp }))
    })

    const isTaxOptionDisabled = taxOption => taxOption.value === 'included' && hasNoContributor.value

    const targetCourse = computed(() => {
      return findVendible(courses.value, props.candidate.courseHistoryTemp.value.vendibleId)
    })

    const courseName = computed(() => {
      return targetCourse.value?.attributes?.name || '延長セット'
    })

    const durationLabel = computed(() => {
      const durationSeconds = targetCourse.value?.attributes?.effectiveDurationSeconds
      if (!durationSeconds) return ''

      return `${Math.round(durationSeconds / 60)}分`
    })

    const lines = computed(() => {
      const { candidate } = props

      const buildLine = (temp, name) => ({
        temp,
        name,
        contributorMissing: temp.value.taxCharge === 'included' && hasNoContributor.value,
      })

      return [
        // カード見出しに同じコース名が出ているため、明細行は重複させず料金種別のラベルにする
        buildLine(candidate.courseHistoryTemp, `${currentClub.value?.courseAlias || 'セット'}料金`),
        ...candidate.nominationHistoriesTemp.map(temp => {
          return buildLine(temp, findVendible(nominations.value, temp.value.vendibleId)?.attributes?.name || '指名')
        }),
        ...candidate.itemHistoriesTemp.map(temp => {
          return buildLine(temp, findVendible(items.value, temp.value.vendibleId)?.attributes?.name || 'アイテム')
        }),
      ]
    })

    return {
      courseName,
      durationLabel,
      lines,
      hasNoContributor,
      isTaxOptionDisabled,
      includedUnavailableHint: INCLUDED_UNAVAILABLE_HINT,
      taxOptions: [
        { value: 'normal', label: '通常' },
        { value: 'included', label: '込み' },
        { value: 'cut', label: 'カット' },
      ],
    }
  },
}
</script>

<style lang="scss" scoped>
.ext-card {
  background: var(--overview-surface-2, #18181f);
  border: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 12px;

  &.is-recommended {
    border-color: var(--overview-accent-soft, rgba(139, 92, 246, 0.45));
  }

  // 設定中のプランは自動延長でも使われる「決まっている内容」なのでアクセント枠で強調する
  &.is-plan-applied {
    border-color: var(--overview-accent, #7c3aed);
  }
}

.ext-plan-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  margin-bottom: 4px;
  border-radius: 999px;
  background: var(--overview-accent-soft, rgba(124, 58, 237, 0.16));
  color: var(--overview-accent, #7c3aed);
}

.ext-plan-note {
  font-size: 12px;
  opacity: 0.6;
  margin-top: 2px;
}

.ext-plan-release {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.ext-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}

.ext-name {
  font-size: 17px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ext-meta {
  font-size: 13px;
  opacity: 0.6;
  margin-top: 2px;
}

.ext-price-area {
  text-align: right;
}

.ext-price {
  font-size: 19px;
  font-weight: 800;
}

.ext-price-sub {
  font-size: 11px;
  opacity: 0.55;
}

.ext-line {
  padding: 8px 0;
  border-top: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
}

.ext-line-main {
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-bottom: 6px;
}

.ext-line-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
}

.ext-line-calc {
  opacity: 0.6;
  margin-right: 10px;
  font-size: 13px;
}

.ext-line-sub {
  font-weight: 700;
}

.ext-tax-toggle {
  background: transparent !important;
}

// タブレット運用での視認性優先。通常/込み/カットの文字を読めるサイズに引き上げる
.ext-tax-toggle .v-btn {
  font-size: 13px;
}

.ext-line-alert {
  margin-top: 6px;
  font-size: 12px;
  color: var(--overview-rose, #e11d48);
}

.ext-actions {
  display: flex;
  gap: 8px;
}

// 主導線は「延長する」。編集は幅を抑えて副次的に見せる
.ext-edit-btn {
  flex: 0 0 auto;
  min-width: 84px;
  height: 40px;
}

.ext-submit-btn {
  flex: 1;
  height: 40px;
}
</style>
